<template>
  <b-overlay :show="loading" opacity="0.5">
    <add-or-update-direct-question-modal
      :editing-data="editingData"
      :is-add-active.sync="toggleModal"
      resource="direct_questions"
      modal-title="Direct Question"
      @create="onCreate"
      @update="onUpdate"
      @refetchData="fetchAllData"
    />

    <div>
      <list-table
        :records="records" 
        :columns="tableColumns"
        :show-import-button="false"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        search-place-holder="Search Disease"
        :current-page-number.sync="currentPage"
        :show-export-button="can('direct_questions:CanExport')"
        @export="onExport"
      >
        <template v-if="can('direct_questions:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="onAddNew"
          >
            <span class="align-middle text-lg">Add new question</span>
          </b-button>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="data.item.status === 'active' ? `light-success` : 'light-danger'"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            
            <b-dropdown-item @click="onEdit(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDelete(data.item)">
              <feather-icon icon="TrashIcon" class="text-danger" />
              <span class="align-middle ml-50 text-danger">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'

import { get, debounce, cloneDeep, startCase } from 'lodash';

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddOrUpdateDirectQuestionModal from '@/@core/components/shared/admin/modals/AddOrUpdateDirectQuestionModal.vue';

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}


export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BButton,
    BDropdown,
    BFormFile,
    BFormInput,
    BPagination,
    BInputGroup, 
    BDropdownItem,
    BInputGroupAppend, 

    vSelect,
    AddOrUpdateDirectQuestionModal
  },
  directives: {
    Ripple,
  },
  data(){
    return {
      loading: false,
      showFilter: false,
      toggleModal: false,

      editingData: null,
      searchFilter: null,

      records: [],
      
      tableColumns: [
        { key: 'question', sortable: false, },
        { key: 'answer_type', sortable: false, formatter: startCase },
        { key: 'select_options', sortable: false },
        { key: 'status' },
        { key: 'created', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'actions' },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    searchFilter: watchHandlerAndResetPageNumber,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);
    this.fetchAllData()

    const { 
      page = 1, 
      search = "", 
      limit = this.perPage 
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = search;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          search: this.searchFilter,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.directQuestions.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.searchFilter,
        }

        const response = await this.useJwt().mainService.directQuestions.export(query);
        FileDownload(response.data, "common-complaints.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async onCreate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to add new record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { payload, save_and_new } = form_data;
        await this.useJwt().mainService.directQuestions.create(payload);
        this.fetchAllData();

        if (save_and_new){
          this.toggleModal = false;
          setTimeout(() => {
            this.toggleModal = true;
          }, 100)
        } else {
          this.toggleModal = false;
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record added successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onUpdate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to update record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { id, payload } = form_data;
        await this.useJwt().mainService.directQuestions.update(id, payload);
        this.toggleModal = false;

        this.fetchAllData();

        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record updated successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onDelete(updating_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to delete this record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        await this.useJwt().mainService.directQuestions.delete(updating_data.id);
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Record deleted successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    onEdit(updating_data){
      this.editingData = cloneDeep(updating_data);
      this.toggleModal = true;
    },
    
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
